import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { formatEuro, formatRating, speedLabel } from "@/lib/format";
import type { Casino } from "@/lib/types";
import { Check, Clock, Star, Wallet } from "lucide-react";

function CtaButton({ casino, className = "" }: { casino: Casino; className?: string }) {
  return (
    <Button
      className={`cta-pulse w-full bg-success text-success-foreground hover:bg-success/90 ${className}`}
      disabled={!casino.affiliate_url}
      asChild={Boolean(casino.affiliate_url)}
    >
      {casino.affiliate_url ? (
        <a href={casino.affiliate_url} target="_blank" rel="sponsored nofollow noopener noreferrer">
          Bezoek casino
        </a>
      ) : (
        <span>Bezoek casino</span>
      )}
    </Button>
  );
}

function RankBadge({ rank }: { rank?: number | undefined }) {
  if (!rank) return null;
  return (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border text-xs font-bold text-muted-foreground">
      {rank}
    </span>
  );
}

function Logo({
  casino,
  compact = false,
  priority = false,
}: {
  casino: Casino;
  compact?: boolean;
  priority?: boolean;
}) {
  return (
    <div
      className={
        compact
          ? "flex h-11 w-24 shrink-0 items-center justify-center rounded-md bg-secondary px-2"
          : "flex h-16 flex-1 items-center justify-center rounded-lg bg-secondary px-3"
      }
    >
      {casino.logo_url ? (
        <img
          src={casino.logo_url}
          alt={`Logo van ${casino.name}`}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          {...(priority ? { fetchPriority: "high" as const } : {})}
          width={compact ? 96 : 160}
          height={compact ? 32 : 48}
          className={`h-auto w-auto max-w-full object-contain ${compact ? "max-h-8" : "max-h-12"}`}
        />
      ) : (
        <span className={`font-semibold text-foreground ${compact ? "text-xs" : "text-sm"}`}>
          {casino.name}
        </span>
      )}
    </div>
  );
}

/**
 * Eén kaart in de vaste casinolijst. Op mobiel bewust compact en simpel
 * (logo + naam + rating, één regel kerncijfers, één grote tikknop); op
 * brede containers de volledige rij met cijfers en pluspunten.
 */
export function CasinoCard({
  casino,
  rank,
  methodSlug,
  methodName,
}: {
  casino: Casino;
  rank?: number;
  methodSlug?: string;
  methodName?: string;
}) {
  const priority = typeof rank === "number" && rank <= 3;
  const payout = casino.typical_withdrawal_time ?? speedLabel(casino.withdrawal_time_hours);
  const paymentLink = methodSlug
    ? casino.links.find((link) => link.method?.slug === methodSlug)
    : undefined;
  const methodUsps = paymentLink && methodName && !casino.supplied_information
    ? [
        `${methodName}-storting: ${paymentLink.estimated_deposit_time ?? "snel verwerkt"}`,
        `Storten vanaf ${formatEuro(paymentLink.deposit_minimum)}`,
        paymentLink.no_known_fees
          ? "Geen bekende stortkosten"
          : `Stortkosten: ${paymentLink.deposit_fee ?? "controleer de kassa"}`,
      ]
    : casino.pros.slice(0, 3);

  return (
    <Card className="@container card-surface relative overflow-hidden transition-shadow hover:shadow-md">
      {casino.promo_label ? (
        <span className="absolute left-0 top-0 z-10 rounded-br-md bg-primary px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-primary-foreground">
          {casino.promo_label}
        </span>
      ) : null}

      {/* Mobiel: grote logo-tegel + bonus + sterren, brede CTA, USP-checks */}
      <div className="flex flex-col gap-3 p-4 pt-8 @3xl:hidden">
        {rank ? (
          <span className="absolute right-3 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground shadow">
            {rank}
          </span>
        ) : null}
        <div className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] items-center gap-3">
          <div className="flex aspect-[16/10] items-center justify-center rounded-xl bg-secondary p-3">
            {casino.logo_url ? (
              <img
                src={casino.logo_url}
                alt={`Logo van ${casino.name}`}
                loading={priority ? "eager" : "lazy"}
                decoding="async"
                {...(priority ? { fetchPriority: "high" as const } : {})}
                width={160}
                height={60}
                className="max-h-full w-auto max-w-full object-contain"
              />
            ) : (
              <span className="text-sm font-semibold text-foreground">{casino.name}</span>
            )}
          </div>
          <div className="min-w-0">
            <p className="line-clamp-3 text-[15px] font-bold leading-snug text-foreground">
              {casino.bonus_text ?? casino.name}
            </p>
            <p className={`mt-1.5 items-center gap-0.5 text-xs text-muted-foreground ${casino.rating == null ? "hidden" : "flex"}`}>
              {[0, 1, 2, 3, 4].map((s) => (
                <Star
                  key={s}
                  className={`h-3.5 w-3.5 ${s < Math.round(casino.rating ?? 0) ? "fill-warning text-warning" : "text-border"}`}
                  aria-hidden
                />
              ))}
              {casino.rating != null ? <span className="ml-1">({formatRating(casino.rating)}/5)</span> : null}
            </p>
          </div>
        </div>

        <CtaButton casino={casino} className="h-12 text-base font-bold" />

        <ul className="space-y-1.5 border-t border-border pt-3">
          {[
            paymentLink && methodName
              ? `${methodName}: ${paymentLink.estimated_deposit_time ?? "snel verwerkt"}`
              : `Uitbetaling: ${payout}`,
            `Min. storting: ${formatEuro(casino.minimum_deposit)}`,
            casino.withdrawal_limit ? `Limiet: ${casino.withdrawal_limit}` : `${casino.links.length} betaalmethoden`,
          ].map((u) => (
            <li key={u} className="flex items-center gap-2 text-sm text-muted-foreground">
              <Check className="h-4 w-4 shrink-0 rounded-full border border-primary p-0.5 text-primary" aria-hidden />
              <span className="truncate">{u}</span>
            </li>
          ))}
        </ul>
        <p className="-mt-1 text-center text-[10px] text-muted-foreground">18+. Voorwaarden gelden.</p>
      </div>

      {/* Desktop/brede kolom: volledige rij */}
      <div className="hidden @3xl:flex @3xl:flex-row @3xl:items-center @3xl:gap-5 @3xl:p-5">
        <div className="flex items-center gap-3 @3xl:w-44">
          <RankBadge rank={rank} />
          <Logo casino={casino} priority={priority} />
        </div>

        <div className="@3xl:w-48">
          <p className="font-semibold text-foreground">{casino.name}</p>
          <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
            <Star className="h-3.5 w-3.5 fill-warning text-warning" aria-hidden />
            {formatRating(casino.rating)} · {casino.links.length} betaalmethoden
          </p>
          {paymentLink && methodName ? (
            <p className="mt-2 text-sm font-medium leading-snug text-foreground">
              {methodName} vanaf {formatEuro(paymentLink.deposit_minimum)}
            </p>
          ) : casino.bonus_text ? (
            <p className="mt-2 text-sm font-medium leading-snug text-foreground">{casino.bonus_text}</p>
          ) : null}
        </div>

        <div className="grid flex-1 grid-cols-2 gap-x-4 gap-y-3 text-sm @5xl:grid-cols-3">
          <div>
            <p className="text-xs text-muted-foreground">Min. storting</p>
            <p className="font-medium text-foreground">{formatEuro(casino.minimum_deposit)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Uitbetaling</p>
            <p className="flex items-center gap-1 font-medium text-foreground">
              <Clock className="h-3.5 w-3.5" aria-hidden />
              {payout}
            </p>
          </div>
          <div className="@5xl:col-span-1">
            <p className="text-xs text-muted-foreground">Limiet</p>
            <p className="flex items-center gap-1 font-medium text-foreground">
              <Wallet className="h-3.5 w-3.5" aria-hidden />
              {casino.withdrawal_limit ?? "—"}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2 @3xl:w-40">
          <CtaButton casino={casino} />
          <p className="text-center text-[11px] text-muted-foreground">18+. Voorwaarden gelden.</p>
        </div>
      </div>

      {casino.information_note ? (
        <p className="border-t border-border px-4 py-2 text-[11px] leading-relaxed text-muted-foreground">
          {casino.information_note}
        </p>
      ) : null}
      {methodUsps.length > 0 ? (
        <div className="hidden border-t border-border bg-muted/40 px-5 py-3 @3xl:block">
          <ul className="flex flex-wrap gap-x-5 gap-y-1">
            {methodUsps.map((pro) => (
              <li key={pro} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Check className="h-3.5 w-3.5 shrink-0 text-success" aria-hidden />
                {pro}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Card>
  );
}
